import {
  keepPreviousData,
  useQuery,
  useInfiniteQuery,
  useMutation,
} from "@tanstack/react-query";
import { IResponse } from "types/api";
import { IProduct } from "types/product";
import FETCHER from "./_fetcher";

export const getProductList = async (
  cateIdList?: string[],
  page?: number,
  limit?: number
) => {
  try {
    let endpoint = `/product?page=${page || 1}&limit=${limit || 20}`;
    if (cateIdList && cateIdList.length > 0) {
      endpoint += `&categoryIds=${cateIdList?.join(",")}`;
    }
    const result = await FETCHER.get<IResponse<IProduct[]>>(endpoint);
    return result.data;
  } catch (e) {
    console.log(e);
  }
};
export const useGetProductList = (
  cateIdList?: string[],
  page?: number,
  limit?: number,
  enabled: boolean = true
) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["getProduct", cateIdList?.join(","), page, limit],
    queryFn: () => getProductList(cateIdList, page, limit),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
  const productList = data?.items.map((product) => ({
    ...product,
    isFavorite: product._count.favorites > 0,
  }));

  return {
    productList: data
      ? {
          items: productList,
          meta: data.meta,
        }
      : undefined,
    isLoading: isFetching || isLoading,
  };
};

export const getProductById = async (id: string) => {
  const result = await FETCHER.get<IProduct>(`/product/${id}`);
  return result.data;
};
export const useGetProductById = (id: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ["getProductById", id],
    queryFn: () => getProductById(id),
    enabled: !!id,
  });
  return {
    productDetail: data,
    isLoading,
  };
};

export const getAutocompleteProduct = async (qs?: string) => {
  const result = await FETCHER.get<IResponse<IProduct[]>>(
    `/product?page=1&limit=10&searchTerm=${qs}`
  );
  return result.data;
};
export const useGetAutocompleteProduct = (qs?: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ["getAutocompleteProduct", qs],
    queryFn: () => getAutocompleteProduct(qs),
    enabled: !!qs,
  });
  return {
    suggestProductList: data?.items,
    isLoading,
  };
};
// const removeDuplicates = <T>(arr: T[], key: keyof T): T[] => {
//   const isAllArrayHaveValue = arr.every((a) => a);
//   if (isAllArrayHaveValue) {
//     const seen = new Set();
//     return arr.filter((item) => {
//       const duplicate = seen.has(item[key]);
//       seen.add(item[key]);
//       return !duplicate;
//     });
//   }
//   return [];
// };

export const getSearchProduct = async (
  qs?: string,
  cateIdList?: string[],
  page?: number,
  limit?: number
) => {
  let endpoint = `/product?page=${page || 1}&limit=${
    limit || 20
  }&searchTerm=${qs}`;

  if (cateIdList && cateIdList.length > 0) {
    endpoint += `&categoryIds=${cateIdList?.join(",")}`;
  }
  const result = await FETCHER.get<IResponse<IProduct[]>>(endpoint);
  return result.data;
};

export const useGetSearchProduct = (
  searchTerm: string,
  cateIdList?: string[],
  page?: number,
  limit?: number
) => {
  const { isPending, data } = useQuery({
    queryKey: [
      "getSearchProduct",
      searchTerm,
      cateIdList?.join(","),
      page,
      limit,
    ],
    queryFn: () => getSearchProduct(searchTerm, cateIdList, page, limit),
    enabled: !!searchTerm,
  });
  const productList = data?.items.map((product) => ({
    ...product,
    isFavorite: product._count.favorites > 0,
  }));
  return {
    productList: productList,
    meta: data?.meta,
    isLoading: isPending,
  };
};

export const useGetSearchProductByGenericName = (genericName: string) => {
  const { isPending, data } = useQuery({
    queryKey: ["getSearchProduct", genericName],
    queryFn: () => getSearchProduct(genericName, undefined, 1, 100),
    enabled: !!genericName,
  });

  const productList = data?.items.map((product) => ({
    ...product,
    isFavorite: product._count.favorites > 0,
  }));
  return {
    productList: productList,
    meta: data?.meta,
    isLoading: isPending,
  };
};

export const getRelatedProductList = async (productId: number) => {
  try {
    const result = await FETCHER.get<IProduct[]>(
      `/product/${productId}/related`
    );
    return result.data;
  } catch (e) {
    console.log(e);
  }
};
export const useGetRelatedProductList = (productId: number) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["getRelatedProduct", productId],
    queryFn: () => getRelatedProductList(productId),
    placeholderData: keepPreviousData,
    enabled: !!productId,
  });

  return {
    productList: data,
    isLoading: isFetching || isLoading,
  };
};

/**
 * Hook for getting product list with infinite scrolling support
 * This is optimized for mobile views where pagination is replaced with infinite scrolling
 */
export const useGetInfiniteProductList = (
  cateIdList?: string[],
  limit: number = 20,
  enabled: boolean = true
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ["getInfiniteProduct", cateIdList?.join(","), limit],
    queryFn: ({ pageParam = 1 }) =>
      getProductList(cateIdList, pageParam, limit),
    initialPageParam: 1,
    enabled: enabled,
    getNextPageParam: (lastPage) => {
      // If we've reached the end, return undefined to signal there are no more pages
      if (
        !lastPage ||
        lastPage.items.length === 0 ||
        lastPage.meta.currentPage >= lastPage.meta.totalPages
      ) {
        return undefined;
      }
      // Otherwise, return the next page number
      return lastPage.meta.currentPage + 1;
    },
  });

  // Flatten the pages of products into a single array
  const allProducts =
    data?.pages.flatMap(
      (page) =>
        page?.items.map((product) => ({
          ...product,
          isFavorite: product._count.favorites > 0,
        })) || []
    ) || [];

  // Get the total count from the most recent page
  const totalItems = data?.pages[data.pages.length - 1]?.meta.totalItems || 0;

  return {
    allProducts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoading || isFetching,
    totalItems,
  };
};

// Request a product that is currently out of stock
export const requestProduct = async (productId: string) => {
  try {
    const result = await FETCHER.post("/product/request-product", {
      productId: Number(productId),
    });
    return result.data;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

// Hook for requesting a product
export const useRequestProduct = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: requestProduct,
  });

  return {
    requestProductAsync: mutateAsync,
    isRequesting: isPending,
  };
};
